import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  Check,
  CircleAlert,
  CircleDashed,
  Loader2,
  Server,
  ShieldCheck,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  createBBConnector,
  listBBConnectors,
  revokeBBConnector,
  updateBBConnector,
  type WebsiteBBConnector,
  type WebsiteBBCreateConnectorInput,
} from '../lib/bb';
import { HTTPStatusError } from '../lib/chat-api';

type CreateDraft = {
  name: string;
  alias: string;
  root: string;
  description: string;
};

type ConnectorEditDraft = CreateDraft;

type SavedBootstrapCommand = {
  connectorId: string;
  command: string;
  savedAt: string;
};

const BOOTSTRAP_COMMANDS_STORAGE_KEY =
  'cueboard.website.bb.bootstrap-commands.v1';

const DEFAULT_DRAFT: CreateDraft = {
  name: '',
  alias: '',
  root: '',
  description: '',
};

function getErrorStatus(error: unknown): number | undefined {
  if (error instanceof HTTPStatusError) {
    return error.status;
  }
  if (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof error.status === 'number'
  ) {
    return error.status;
  }
  return undefined;
}

function formatDateTime(value?: string | null) {
  if (!value) {
    return 'Never';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Unknown';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
}

function readSavedBootstrapCommands(): Record<string, SavedBootstrapCommand> {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(BOOTSTRAP_COMMANDS_STORAGE_KEY);
    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') {
      return {};
    }

    const next: Record<string, SavedBootstrapCommand> = {};
    for (const [connectorId, value] of Object.entries(parsed)) {
      if (
        typeof value === 'object' &&
        value !== null &&
        'command' in value &&
        typeof value.command === 'string' &&
        'savedAt' in value &&
        typeof value.savedAt === 'string'
      ) {
        next[connectorId] = {
          connectorId,
          command: value.command,
          savedAt: value.savedAt,
        };
      }
    }
    return next;
  } catch {
    return {};
  }
}

function writeSavedBootstrapCommands(
  commands: Record<string, SavedBootstrapCommand>
) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      BOOTSTRAP_COMMANDS_STORAGE_KEY,
      JSON.stringify(commands)
    );
  } catch {
    // Ignore storage failures.
  }
}

function compareDateDesc(left?: string | null, right?: string | null) {
  const leftTime = left ? new Date(left).getTime() : 0;
  const rightTime = right ? new Date(right).getTime() : 0;
  return rightTime - leftTime;
}

function sortConnectors(connectors: WebsiteBBConnector[]) {
  return [...connectors].sort((left, right) => {
    const seenDiff = compareDateDesc(left.last_seen_at, right.last_seen_at);
    if (seenDiff !== 0) {
      return seenDiff;
    }
    return compareDateDesc(left.created_at, right.created_at);
  });
}

function statusCopy(connector: WebsiteBBConnector) {
  switch (connector.status) {
    case 'active':
      if (connector.online === true) {
        return {
          label: 'Online',
          tone: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
          detail: connector.last_env_id?.trim()
            ? `Connected through ${connector.last_env_id}`
            : 'Long-lived bb connection is alive',
        } as const;
      }
      return {
        label: 'Offline',
        tone: 'bg-slate-500/10 text-slate-700 dark:text-slate-300',
        detail: connector.environment_status?.trim()
          ? `Last environment status: ${connector.environment_status}`
          : 'Installed, but no live bb connection is currently available',
      } as const;
    case 'revoked':
      return {
        label: 'Revoked',
        tone: 'bg-rose-500/10 text-rose-700 dark:text-rose-400',
        detail: 'Access revoked. This computer can no longer connect.',
      } as const;
    case 'pending':
    default:
      return {
        label: 'Pending',
        tone: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
        detail: 'Waiting for the one-time bootstrap command to run.',
      } as const;
  }
}

function isGeneratedPendingConnector(connector: WebsiteBBConnector) {
  if (connector.status !== 'pending') {
    return false;
  }
  return (
    /^bb-\d{8}-\d{6}$/.test(connector.name) &&
    connector.root === '$HOME' &&
    connector.alias === connector.name.toLowerCase()
  );
}

function connectorDisplayName(connector: WebsiteBBConnector) {
  if (isGeneratedPendingConnector(connector)) {
    return 'Auto-detect on first connect';
  }
  return connector.name;
}

function connectorDisplayAlias(connector: WebsiteBBConnector) {
  if (isGeneratedPendingConnector(connector)) {
    return '';
  }
  return connector.alias;
}

function createEditDraft(connector: WebsiteBBConnector): ConnectorEditDraft {
  return {
    name: connector.name,
    alias: connector.alias,
    root: connector.root,
    description: connector.description ?? '',
  };
}

function platformLabel(connector: WebsiteBBConnector) {
  const parts = [connector.platform, connector.arch, connector.version]
    .map(value => value?.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : 'Not reported yet';
}

function StatCard({
  label,
  value,
  detail,
  icon,
}: {
  label: string;
  value: string;
  detail: string;
  icon: ReactNode;
}) {
  return (
    <Card size="sm" className="gap-4">
      <CardContent className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
            {label}
          </div>
          <div className="mt-1 text-2xl font-semibold tracking-tight text-foreground">
            {value}
          </div>
          <div className="mt-1 text-sm text-muted-foreground">{detail}</div>
        </div>
        <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
          {icon}
        </div>
      </CardContent>
    </Card>
  );
}

export function ComputersPage() {
  const navigate = useNavigate();
  const [connectors, setConnectors] = useState<WebsiteBBConnector[]>([]);
  const [draft, setDraft] = useState<CreateDraft>(DEFAULT_DRAFT);
  const [savedCommands, setSavedCommands] = useState<
    Record<string, SavedBootstrapCommand>
  >(() => readSavedBootstrapCommands());
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateOverrides, setShowCreateOverrides] = useState(false);
  const [editingConnectorId, setEditingConnectorId] = useState<string | null>(
    null
  );
  const [editDraft, setEditDraft] = useState<ConnectorEditDraft | null>(null);
  const [savingConnectorId, setSavingConnectorId] = useState<string | null>(
    null
  );
  const [revokingConnectorId, setRevokingConnectorId] = useState<string | null>(
    null
  );
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [copiedCommandId, setCopiedCommandId] = useState<string | null>(null);

  const redirectToLogin = useCallback(() => {
    navigate(`/login?next=${encodeURIComponent('/settings/computers')}`, {
      replace: true,
    });
  }, [navigate]);

  const persistCommand = useCallback((connectorId: string, command: string) => {
    setSavedCommands(current => ({
      ...current,
      [connectorId]: {
        connectorId,
        command,
        savedAt: new Date().toISOString(),
      },
    }));
  }, []);

  const removeCommand = useCallback((connectorId: string) => {
    setSavedCommands(current => {
      if (!current[connectorId]) {
        return current;
      }
      const next = { ...current };
      delete next[connectorId];
      return next;
    });
  }, []);

  const loadPage = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const nextConnectors = await listBBConnectors();
      setConnectors(sortConnectors(nextConnectors));
    } catch (loadError) {
      if (getErrorStatus(loadError) === 401) {
        redirectToLogin();
        return;
      }
      setError(
        loadError instanceof Error
          ? loadError.message
          : 'Failed to load connected computers'
      );
    } finally {
      setLoading(false);
    }
  }, [redirectToLogin]);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  useEffect(() => {
    writeSavedBootstrapCommands(savedCommands);
  }, [savedCommands]);

  useEffect(() => {
    if (!editingConnectorId) {
      return;
    }
    const editingConnector = connectors.find(
      connector => connector.id === editingConnectorId
    );
    if (!editingConnector || editingConnector.status !== 'active') {
      setEditingConnectorId(null);
      setEditDraft(null);
    }
  }, [connectors, editingConnectorId]);

  useEffect(() => {
    if (loading) {
      return;
    }

    setSavedCommands(current => {
      let changed = false;
      const next: Record<string, SavedBootstrapCommand> = {};
      for (const connector of connectors) {
        const saved = current[connector.id];
        if (!saved) {
          continue;
        }
        if (connector.status !== 'pending') {
          changed = true;
          continue;
        }
        next[connector.id] = saved;
      }

      if (
        !changed &&
        Object.keys(next).length === Object.keys(current).length
      ) {
        return current;
      }
      return next;
    });
  }, [connectors, loading]);

  const connectorById = useMemo(() => {
    return new Map(
      connectors.map(connector => [connector.id, connector] as const)
    );
  }, [connectors]);

  const latestSavedCommand = useMemo(() => {
    return (
      Object.values(savedCommands)
        .filter(
          saved => connectorById.get(saved.connectorId)?.status === 'pending'
        )
        .sort((left, right) =>
          compareDateDesc(left.savedAt, right.savedAt)
        )[0] ?? null
    );
  }, [connectorById, savedCommands]);

  const totalCount = connectors.length;
  const onlineCount = connectors.filter(
    connector => connector.online === true
  ).length;
  const pendingCount = connectors.filter(
    connector => connector.status === 'pending'
  ).length;
  const revokedCount = connectors.filter(
    connector => connector.status === 'revoked'
  ).length;

  const handleCopyCommand = useCallback(
    async (connectorId: string, command: string) => {
      try {
        await navigator.clipboard.writeText(command);
        setCopiedCommandId(connectorId);
        setNotice(
          'Install command copied. Run it on the target computer to install and start bb.'
        );
        setError('');
        window.setTimeout(() => {
          setCopiedCommandId(current =>
            current === connectorId ? null : current
          );
        }, 2000);
      } catch {
        setError(
          'Failed to copy the install command. Copy it manually from the page.'
        );
      }
    },
    []
  );

  const handleCreate = useCallback(async () => {
    const payload: WebsiteBBCreateConnectorInput = {
      name: draft.name.trim() || undefined,
      alias: draft.alias.trim() || undefined,
      root: draft.root.trim() || undefined,
      description: draft.description.trim() || undefined,
    };

    setCreating(true);
    setError('');
    setNotice('');

    try {
      const result = await createBBConnector(payload);
      persistCommand(result.connector.id, result.suggested_command);
      setConnectors(current =>
        sortConnectors([
          result.connector,
          ...current.filter(connector => connector.id !== result.connector.id),
        ])
      );
      setDraft(DEFAULT_DRAFT);
      setShowCreateOverrides(false);
      setNotice(
        `Install command ready for ${connectorDisplayName(result.connector)}. Copy it and run it on the target computer.`
      );
    } catch (createError) {
      if (getErrorStatus(createError) === 401) {
        redirectToLogin();
        return;
      }
      setError(
        createError instanceof Error
          ? createError.message
          : 'Failed to create the computer connector'
      );
    } finally {
      setCreating(false);
    }
  }, [draft, persistCommand, redirectToLogin]);

  const startEditingConnector = useCallback((connector: WebsiteBBConnector) => {
    setEditingConnectorId(connector.id);
    setEditDraft(createEditDraft(connector));
    setError('');
    setNotice('');
  }, []);

  const cancelEditingConnector = useCallback(() => {
    setEditingConnectorId(null);
    setEditDraft(null);
  }, []);

  const handleSaveConnector = useCallback(async () => {
    if (!editingConnectorId || !editDraft) {
      return;
    }

    setSavingConnectorId(editingConnectorId);
    setError('');
    setNotice('');
    try {
      const updated = await updateBBConnector(editingConnectorId, {
        name: editDraft.name.trim(),
        alias: editDraft.alias.trim() || undefined,
        root: editDraft.root.trim(),
        description: editDraft.description.trim() || undefined,
      });
      setConnectors(current =>
        sortConnectors(
          current.map(existing =>
            existing.id === updated.id ? updated : existing
          )
        )
      );
      setEditingConnectorId(null);
      setEditDraft(null);
      setNotice(
        `${updated.name} was updated. It will sync on the next bb reconnect.`
      );
    } catch (saveError) {
      if (getErrorStatus(saveError) === 401) {
        redirectToLogin();
        return;
      }
      setError(
        saveError instanceof Error
          ? saveError.message
          : 'Failed to update the computer connector'
      );
    } finally {
      setSavingConnectorId(null);
    }
  }, [editDraft, editingConnectorId, redirectToLogin]);

  const handleRevoke = useCallback(
    async (connector: WebsiteBBConnector) => {
      const confirmed = window.confirm(
        `Revoke ${connector.name}? This computer will stop connecting through Cueboard.`
      );
      if (!confirmed) {
        return;
      }

      setRevokingConnectorId(connector.id);
      setError('');
      setNotice('');
      try {
        const updated = await revokeBBConnector(connector.id);
        removeCommand(connector.id);
        setConnectors(current =>
          sortConnectors(
            current.map(existing =>
              existing.id === updated.id ? updated : existing
            )
          )
        );
        if (editingConnectorId === connector.id) {
          setEditingConnectorId(null);
          setEditDraft(null);
        }
        setNotice(`${updated.name} was revoked.`);
      } catch (revokeError) {
        if (getErrorStatus(revokeError) === 401) {
          redirectToLogin();
          return;
        }
        setError(
          revokeError instanceof Error
            ? revokeError.message
            : 'Failed to revoke the computer connector'
        );
      } finally {
        setRevokingConnectorId(null);
      }
    },
    [editingConnectorId, redirectToLogin, removeCommand]
  );

  return (
    <div className="flex flex-col gap-6">
      {notice ? (
        <div className="flex items-start gap-2 rounded-xl border border-emerald-200/70 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300">
          <Check className="mt-0.5 size-4 shrink-0" />
          <span>{notice}</span>
        </div>
      ) : null}

      {error ? (
        <div className="flex items-start gap-2 rounded-xl border border-rose-200/70 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300">
          <CircleAlert className="mt-0.5 size-4 shrink-0" />
          <span>{error}</span>
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-3">
        <StatCard
          label="Computers"
          value={String(totalCount)}
          detail={
            totalCount === 1 ? 'connector configured' : 'connectors configured'
          }
          icon={<Server className="size-5" />}
        />
        <StatCard
          label="Online"
          value={String(onlineCount)}
          detail={
            onlineCount === 1
              ? 'computer with a live bb connection'
              : 'computers with live bb connections'
          }
          icon={<ShieldCheck className="size-5" />}
        />
        <StatCard
          label="Needs setup"
          value={String(pendingCount)}
          detail={
            revokedCount > 0
              ? `${revokedCount} revoked`
              : 'Awaiting bootstrap on the target machine'
          }
          icon={<CircleDashed className="size-5" />}
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.05fr)_minmax(320px,0.95fr)]">
        <Card>
          <CardHeader>
            <CardTitle>Create a computer connector</CardTitle>
            <CardDescription>
              Generate a one-time bootstrap command for a computer you control.
              By default bb auto-detects the computer details on the target
              machine, and you can edit them here after it connects.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-xl border border-border/70 bg-muted/20 px-4 py-4 text-sm text-muted-foreground">
              Leave the overrides blank to let bb detect the computer name,
              alias, shared root, and description on first connect.
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/70 bg-background px-4 py-3">
              <div>
                <div className="text-sm font-medium text-foreground">
                  Optional overrides
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Use these only if you want to override the machine defaults.
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowCreateOverrides(current => !current);
                }}
              >
                {showCreateOverrides ? 'Hide overrides' : 'Show overrides'}
              </Button>
            </div>

            {showCreateOverrides ? (
              <div className="space-y-4 rounded-xl border border-border/70 bg-background px-4 py-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <label className="space-y-2">
                    <div className="text-sm font-medium text-foreground">
                      Computer name override
                    </div>
                    <Input
                      value={draft.name}
                      placeholder="My MacBook Pro"
                      onChange={event => {
                        setDraft(current => ({
                          ...current,
                          name: event.target.value,
                        }));
                      }}
                    />
                  </label>

                  <label className="space-y-2">
                    <div className="text-sm font-medium text-foreground">
                      Alias override
                    </div>
                    <Input
                      value={draft.alias}
                      placeholder="macbook-pro"
                      onChange={event => {
                        setDraft(current => ({
                          ...current,
                          alias: event.target.value,
                        }));
                      }}
                    />
                  </label>
                </div>

                <label className="space-y-2">
                  <div className="text-sm font-medium text-foreground">
                    Shared root override
                  </div>
                  <Input
                    value={draft.root}
                    placeholder="Leave blank to auto-detect"
                    onChange={event => {
                      setDraft(current => ({
                        ...current,
                        root: event.target.value,
                      }));
                    }}
                  />
                  <div className="text-xs text-muted-foreground">
                    bb will only serve files under this root on the target
                    machine.
                  </div>
                </label>

                <label className="space-y-2">
                  <div className="text-sm font-medium text-foreground">
                    Description override
                  </div>
                  <textarea
                    value={draft.description}
                    rows={3}
                    placeholder="Optional note to help you remember which machine this is for"
                    className="flex min-h-[88px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none transition-colors duration-200 placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/20"
                    onChange={event => {
                      setDraft(current => ({
                        ...current,
                        description: event.target.value,
                      }));
                    }}
                  />
                </label>
              </div>
            ) : null}

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/70 pt-4">
              <div className="text-sm text-muted-foreground">
                The install command is one-time. Keep it until the computer
                finishes its first bb connect.
              </div>
              <Button
                disabled={creating}
                onClick={() => {
                  void handleCreate();
                }}
              >
                {creating ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Server className="size-4" />
                )}
                Create install command
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Install + bootstrap command</CardTitle>
            <CardDescription>
              Run this on the target machine. It installs bb from Cueboard R2,
              bootstraps once, and then starts bb.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {latestSavedCommand ? (
              (() => {
                const connector = connectorById.get(
                  latestSavedCommand.connectorId
                );
                return (
                  <>
                    <div className="rounded-xl border border-border/70 bg-muted/30 p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-medium text-foreground">
                            {connector
                              ? connectorDisplayName(connector)
                              : 'Pending computer'}
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            Saved locally{' '}
                            {formatDateTime(latestSavedCommand.savedAt)}
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            void handleCopyCommand(
                              latestSavedCommand.connectorId,
                              latestSavedCommand.command
                            );
                          }}
                        >
                          {copiedCommandId === latestSavedCommand.connectorId
                            ? 'Copied'
                            : 'Copy command'}
                        </Button>
                      </div>
                      <pre className="mt-4 overflow-x-auto rounded-lg bg-neutral-950 px-4 py-3 text-xs leading-6 text-neutral-100">
                        {latestSavedCommand.command}
                      </pre>
                    </div>

                    <div className="rounded-xl border border-border/70 bg-background px-4 py-4 text-sm text-muted-foreground">
                      <div className="font-medium text-foreground">
                        Recommended flow
                      </div>
                      <ol className="mt-3 space-y-2 list-decimal pl-5">
                        <li>Run the install command once on that computer.</li>
                        <li>
                          Leave <code>bb run</code> active so Cueboard can proxy
                          through it after bb finishes bootstrapping.
                        </li>
                      </ol>
                    </div>
                  </>
                );
              })()
            ) : (
              <div className="rounded-xl border border-dashed border-border/80 bg-muted/20 px-4 py-6 text-sm text-muted-foreground">
                Create a connector to get the one-time bootstrap command. The
                latest pending command stays available in this browser until the
                computer connects or you revoke it.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Managed computers</CardTitle>
          <CardDescription>
            Review connection state, copy any still-pending bootstrap command
            from this browser, edit connected computer details, and revoke
            machines that should stop reaching Willow.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 rounded-xl border border-border/70 bg-muted/20 px-4 py-6 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Loading connected computers…
            </div>
          ) : connectors.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border/80 bg-muted/20 px-4 py-8 text-center text-sm text-muted-foreground">
              No computers configured yet.
            </div>
          ) : (
            connectors.map(connector => {
              const copy = statusCopy(connector);
              const savedCommand = savedCommands[connector.id] ?? null;
              const isRevoking = revokingConnectorId === connector.id;
              const isEditing = editingConnectorId === connector.id;
              const isSaving = savingConnectorId === connector.id;
              const displayName = connectorDisplayName(connector);
              const displayAlias = connectorDisplayAlias(connector);
              const rootCopy = isGeneratedPendingConnector(connector)
                ? 'Auto-detected after first connect'
                : connector.root;

              return (
                <div
                  key={connector.id}
                  className="rounded-xl border border-border/70 bg-background px-4 py-4"
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0 flex-1 space-y-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="text-base font-semibold text-foreground">
                          {displayName}
                        </div>
                        <span
                          className={cn(
                            'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium',
                            copy.tone
                          )}
                        >
                          {copy.label}
                        </span>
                        {displayAlias ? (
                          <span className="rounded-full border border-border/70 bg-muted/30 px-2.5 py-1 text-xs text-muted-foreground">
                            {displayAlias}
                          </span>
                        ) : null}
                      </div>

                      {!isEditing && connector.description ? (
                        <div className="text-sm text-muted-foreground">
                          {connector.description}
                        </div>
                      ) : null}

                      {isEditing && editDraft ? (
                        <div className="space-y-4 rounded-xl border border-border/70 bg-muted/20 px-4 py-4">
                          <div className="grid gap-4 md:grid-cols-2">
                            <label className="space-y-2">
                              <div className="text-sm font-medium text-foreground">
                                Computer name
                              </div>
                              <Input
                                value={editDraft.name}
                                onChange={event => {
                                  setEditDraft(current =>
                                    current
                                      ? {
                                          ...current,
                                          name: event.target.value,
                                        }
                                      : current
                                  );
                                }}
                              />
                            </label>
                            <label className="space-y-2">
                              <div className="text-sm font-medium text-foreground">
                                Alias
                              </div>
                              <Input
                                value={editDraft.alias}
                                onChange={event => {
                                  setEditDraft(current =>
                                    current
                                      ? {
                                          ...current,
                                          alias: event.target.value,
                                        }
                                      : current
                                  );
                                }}
                              />
                            </label>
                          </div>

                          <label className="space-y-2">
                            <div className="text-sm font-medium text-foreground">
                              Shared root path
                            </div>
                            <Input
                              value={editDraft.root}
                              onChange={event => {
                                setEditDraft(current =>
                                  current
                                    ? {
                                        ...current,
                                        root: event.target.value,
                                      }
                                    : current
                                );
                              }}
                            />
                          </label>

                          <label className="space-y-2">
                            <div className="text-sm font-medium text-foreground">
                              Description
                            </div>
                            <textarea
                              value={editDraft.description}
                              rows={3}
                              className="flex min-h-[88px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none transition-colors duration-200 placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/20"
                              onChange={event => {
                                setEditDraft(current =>
                                  current
                                    ? {
                                        ...current,
                                        description: event.target.value,
                                      }
                                    : current
                                );
                              }}
                            />
                          </label>

                          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/70 pt-4">
                            <div className="text-xs text-muted-foreground">
                              Changes sync to the local bb client on the next
                              reconnect.
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  cancelEditingConnector();
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                disabled={isSaving}
                                onClick={() => {
                                  void handleSaveConnector();
                                }}
                              >
                                {isSaving ? (
                                  <Loader2 className="size-4 animate-spin" />
                                ) : null}
                                Save details
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="grid gap-3 text-sm text-muted-foreground sm:grid-cols-2 xl:grid-cols-4">
                          <div>
                            <div className="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground/80">
                              Root
                            </div>
                            <div className="mt-1 break-all text-foreground">
                              {rootCopy}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground/80">
                              Platform
                            </div>
                            <div className="mt-1 text-foreground">
                              {platformLabel(connector)}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground/80">
                              Last seen
                            </div>
                            <div className="mt-1 text-foreground">
                              {formatDateTime(connector.last_seen_at)}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground/80">
                              Env
                            </div>
                            <div className="mt-1 break-all text-foreground">
                              {connector.last_env_id?.trim() ||
                                'Not assigned yet'}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="rounded-xl border border-border/70 bg-muted/20 px-3 py-3 text-sm text-muted-foreground">
                        {copy.detail}
                      </div>

                      {connector.status === 'pending' && savedCommand ? (
                        <div className="rounded-xl border border-border/70 bg-neutral-950 px-4 py-3 text-neutral-100">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="text-sm font-medium">
                              One-time bootstrap command
                            </div>
                            <div className="text-xs text-neutral-400">
                              Saved {formatDateTime(savedCommand.savedAt)}
                            </div>
                          </div>
                          <pre className="mt-3 overflow-x-auto text-xs leading-6 text-neutral-100">
                            {savedCommand.command}
                          </pre>
                        </div>
                      ) : null}

                      {connector.status === 'pending' && !savedCommand ? (
                        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-3 text-sm text-amber-800 dark:text-amber-300">
                          The bootstrap command is only available in the browser
                          that created it. If you lost it, revoke this connector
                          and create a new one.
                        </div>
                      ) : null}
                    </div>

                    <div className="flex shrink-0 flex-wrap gap-2">
                      {connector.status === 'pending' && savedCommand ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            void handleCopyCommand(
                              connector.id,
                              savedCommand.command
                            );
                          }}
                        >
                          {copiedCommandId === connector.id
                            ? 'Copied'
                            : 'Copy command'}
                        </Button>
                      ) : null}

                      {connector.status === 'active' && !isEditing ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            startEditingConnector(connector);
                          }}
                        >
                          Edit details
                        </Button>
                      ) : null}

                      {connector.status !== 'revoked' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={isRevoking || isSaving}
                          onClick={() => {
                            void handleRevoke(connector);
                          }}
                        >
                          {isRevoking ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : null}
                          Revoke access
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
}
